import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Box, Typography, TextField, Button, Grid, IconButton,
    Card, Switch, InputAdornment, Chip, Tab, Tabs,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Avatar,
    CircularProgress, Tooltip,
} from '@mui/material';
import http, { apiErrorMessage } from '../../../Api/http';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SecurityIcon from '@mui/icons-material/Security';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { useSelector } from 'react-redux';
import { selectWebsiteSettings } from '../../../redux/slices/websiteSettingsSlice';
import SnackBar from '../../SnackBar';
import {
    employeeComplianceDashboard, postPFConfiguration, postESIConfiguration,
    postProfessionalTaxConfiguration, postTDSConfiguration, getDeductionsAndCompliance,
    updateEmployeeComplianceByEmployeeCode, getSalaryComponentConfig, postSalaryComponentConfig,
} from '../../../Api/Api';
import { SkeletonRows } from '../../uiKit';
import { useCrumbActionsSetter } from '../../../utils/crumbActions';
import * as XLSX from 'xlsx';

const PRIMARY = '#135F93';
const PRIMARY_LIGHT = '#E4F0F9';
const PRIMARY_DARK = '#0E4A75';
const PRIMARY_BORDER = '#B3D3E8';

const AVATAR_PALETTE = ['#0E7490', '#135F93', '#C2410C', '#0E4A75', '#1D4ED8', '#BE185D', '#A16207', '#0F766E'];
const avatarColorFor = (name = '') => {
    const code = (name.charCodeAt(0) || 0) + (name.charCodeAt(1) || 0);
    return AVATAR_PALETTE[code % AVATAR_PALETTE.length];
};

const getInitials = (name = '') =>
    name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

const formatINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const DEFAULT_SPLIT = { basicPercent: 45, daPercent: 5, hraPercent: 20, conveyancePercent: 5, specialPercent: 25 };
const SPLIT_FIELDS = [
    { key: 'basicPercent', label: 'Basic', short: 'Basic' },
    { key: 'daPercent', label: 'DA', short: 'DA' },
    { key: 'hraPercent', label: 'HRA', short: 'HRA' },
    { key: 'conveyancePercent', label: 'Conveyance', short: 'Conveyance' },
    { key: 'specialPercent', label: 'Special', short: 'Special' },
];

export default function ComplianceSettings() {
    const websiteSettings = useSelector(selectWebsiteSettings);
    const [activeTab, setActiveTab] = useState(() => (window.location.hash === '#components' ? 4 : 0));

    const [snackOpen, setSnackOpen] = useState(false);
    const [snackStatus, setSnackStatus] = useState(false);
    const [snackColor, setSnackColor] = useState(false);
    const [snackMessage, setSnackMessage] = useState('');
    const showSnack = (msg, success) => {
        setSnackMessage(msg); setSnackOpen(true); setSnackColor(success); setSnackStatus(success);
    };

    const [pfSettings, setPfSettings] = useState({
        enabled: true,
        employeeContribution: 12,
        employerContribution: 12,
        wageLimit: 15000,
        adminCharges: 0.5,
        edliCharges: 0.5,
        eligibilityEnabled: false,
        eligibilityMonths: 3,
    });

    const [esiSettings, setEsiSettings] = useState({
        enabled: true,
        employeeContribution: 0.75,
        employerContribution: 3.25,
        wageLimit: 21000,
        eligibilityEnabled: false,
        eligibilityMonths: 3,
    });

    const [ptSettings, setPtSettings] = useState({
        enabled: true,
        monthlyDeduction: 200,
        annualDeduction: 2400,
        applicableFrom: 15000,
    });

    const [tdsSettings, setTdsSettings] = useState({
        enabled: true,
        standardDeduction: 50000,
        section80C: 150000,
        section80D: 25000,
        hraExemption: true,
    });

    const [stats, setStats] = useState({
        totalEmployees: 0,
        pfApplicableCount: 0,
        esiApplicableCount: 0,
        ptApplicableCount: 0,
    });

    const [employeeData, setEmployeeData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isPFSaving, setIsPFSaving] = useState(false);
    const [isESISaving, setIsESISaving] = useState(false);
    const [isPTSaving, setIsPTSaving] = useState(false);
    const [isTDSSaving, setIsTDSSaving] = useState(false);
    const [isSplitSaving, setIsSplitSaving] = useState(false);
    const [savingFlags, setSavingFlags] = useState({});
    const [companyCfg, setCompanyCfg] = useState(null);
    const [split, setSplit] = useState(DEFAULT_SPLIT);
    const [splitMeta, setSplitMeta] = useState({ isDefault: true, updatedBy: '', updatedOn: '' });
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        fetchComplianceDashboard();
        fetchDeductionsConfig();
        fetchSalaryComponents();
    }, []);

    const fetchDeductionsConfig = async () => {
        try {
            const res = await http.get(getDeductionsAndCompliance);
            if (res.data && !res.data.error) {
                const d = res.data.data;
                setCompanyCfg(d);
                setPfSettings(prev => ({
                    ...prev,
                    enabled:             (d.pfEnabled ?? prev.enabled),
                    employeeContribution: Number(d.pfEmployeeContribution) || prev.employeeContribution,
                    employerContribution: Number(d.pfEmployerContribution) || prev.employerContribution,
                    wageLimit:            Number(d.pfWageCeilingLimit)     || prev.wageLimit,
                    adminCharges:         Number(d.pfAdminCharges)         || prev.adminCharges,
                    eligibilityEnabled:  (d.pfWaitingPeriodEnabled ?? d.pfApplyAfterJoining ?? d.pfEligibilityEnabled) ?? prev.eligibilityEnabled,
                    eligibilityMonths:   Number(d.pfWaitingPeriodMonths ?? d.pfEligibilityMonths) || prev.eligibilityMonths,
                }));
                setEsiSettings(prev => ({
                    ...prev,
                    enabled:             (d.esiEnabled ?? prev.enabled),
                    employeeContribution: Number(d.esiEmployeeContribution) || prev.employeeContribution,
                    employerContribution: Number(d.esiEmployerContribution) || prev.employerContribution,
                    wageLimit:            Number(d.esiWageCeilingLimit)     || prev.wageLimit,
                    eligibilityEnabled:  (d.esiWaitingPeriodEnabled ?? d.esiApplyAfterJoining ?? d.esiEligibilityEnabled) ?? prev.eligibilityEnabled,
                    eligibilityMonths:   Number(d.esiWaitingPeriodMonths ?? d.esiEligibilityMonths) || prev.eligibilityMonths,
                }));
                setPtSettings(prev => ({
                    ...prev,
                    enabled:          (d.ptEnabled ?? prev.enabled),
                    monthlyDeduction: Number(d.monthlyPTDeduction)   || prev.monthlyDeduction,
                    annualDeduction:  Number(d.annualPTDeduction)    || prev.annualDeduction,
                    applicableFrom:   Number(d.applicableFromSalary) || prev.applicableFrom,
                }));
                setTdsSettings(prev => ({
                    ...prev,
                    standardDeduction: Number(d.standardDeduction) || prev.standardDeduction,
                    section80C:        Number(d.section80cLimit)   || prev.section80C,
                    section80D:        Number(d.section80DLimit)   || prev.section80D,
                    hraExemption:      d.hraExemption === 'Y',
                }));
            }
        } catch {
        }
    };

    const fetchComplianceDashboard = async () => {
        setIsLoading(true);
        try {
            const res = await http.get(employeeComplianceDashboard);
            if (res.data && !res.data.error) {
                const d = res.data.data;
                setStats({
                    totalEmployees: d.totalEmployees,
                    pfApplicableCount: d.pfApplicable,
                    esiApplicableCount: d.esiApplicable,
                    ptApplicableCount: d.ptApplicable,
                });
                setEmployeeData((d.employees || []).map(emp => ({
                    id: emp.id ?? emp.employeeCode,
                    employeeCode: emp.employeeCode,
                    employeeId: emp.employeeCode ?? emp.rollNumber,
                    rollNumber: emp.employeeCode ?? emp.rollNumber,
                    name: emp.name,
                    designation: emp.designation,
                    payType: emp.payType || 'Monthly',
                    monthlyGross: emp.monthlyGross ?? 0,
                    isPFApplicable: !!emp.isPFApplicable,
                    isESIApplicable: !!emp.isESIApplicable,
                    isPTApplicable: !!emp.isPTApplicable,
                })));
            } else {
                showSnack('Failed to load compliance data', false);
            }
        } catch (err) {
            if (err?.response?.status === 404) {
                setEmployeeData([]);
            } else {
                showSnack('Failed to load compliance data', false);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const fetchSalaryComponents = async () => {
        try {
            const res = await http.get(getSalaryComponentConfig);
            if (res.data && !res.data.error) {
                const d = res.data.data;
                setSplit({
                    basicPercent: d.basicPercent, daPercent: d.daPercent, hraPercent: d.hraPercent,
                    conveyancePercent: d.conveyancePercent, specialPercent: d.specialPercent,
                });
                setSplitMeta({ isDefault: !!d.isDefault, updatedBy: d.updatedBy, updatedOn: d.updatedOn });
            }
        } catch {
            // keep the built-in defaults
        }
    };

    const splitTotal = SPLIT_FIELDS.reduce((a, f) => a + (Number(split[f.key]) || 0), 0);
    const splitValid = Math.abs(splitTotal - 100) < 0.005;

    const filteredEmployees = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return employeeData;
        return employeeData.filter(emp =>
            (emp.name || '').toLowerCase().includes(q) ||
            (emp.employeeId || '').toLowerCase().includes(q) ||
            (emp.designation || '').toLowerCase().includes(q)
        );
    }, [employeeData, searchQuery]);

    const searchActive = searchQuery.trim().length > 0;

    // Flip one statutory flag for an employee and save straight away.
    const handleToggleFlag = async (emp, flag, checked) => {
        const savingKey = `${emp.employeeCode}:${flag}`;
        setSavingFlags(prev => ({ ...prev, [savingKey]: true }));
        setEmployeeData(prev => prev.map(e => e.employeeCode === emp.employeeCode ? { ...e, [flag]: checked } : e));
        const bump = { isPFApplicable: 'pfApplicableCount', isESIApplicable: 'esiApplicableCount', isPTApplicable: 'ptApplicableCount' }[flag];
        const revert = () => {
            setEmployeeData(prev => prev.map(e => e.employeeCode === emp.employeeCode ? { ...e, [flag]: !checked } : e));
        };
        try {
            const res = await http.put(updateEmployeeComplianceByEmployeeCode, {
                EmployeeCode: emp.employeeCode,
                [flag.charAt(0).toUpperCase() + flag.slice(1)]: checked,
            });
            if (res.data && !res.data.error) {
                setStats(prev => ({ ...prev, [bump]: Math.max(0, prev[bump] + (checked ? 1 : -1)) }));
            } else {
                revert();
                showSnack(res.data?.message || 'Failed to update compliance settings', false);
            }
        } catch (err) {
            revert();
            showSnack(
                err?.response?.status === 404
                    ? 'Create a salary structure first'
                    : apiErrorMessage(err, 'Failed to update compliance settings. Please try again.'),
                false,
            );
        } finally {
            setSavingFlags(prev => ({ ...prev, [savingKey]: false }));
        }
    };

    const handleExportCompliance = () => {
        const excelData = employeeData.map((emp, index) => ({
            'S.No': index + 1,
            'Employee ID': emp.employeeId,
            'Employee Name': emp.name,
            'Designation': emp.designation,
            'Pay Type': emp.payType,
            'Monthly Gross': emp.monthlyGross,
            'PF Applicable': emp.isPFApplicable ? 'Yes' : 'No',
            'ESI Applicable': emp.isESIApplicable ? 'Yes' : 'No',
            'PT Applicable': emp.isPTApplicable ? 'Yes' : 'No',
        }));

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(excelData);
        ws['!cols'] = [6, 12, 20, 18, 10, 14, 14, 14, 14].map(wch => ({ wch }));
        XLSX.utils.book_append_sheet(wb, ws, 'Employee Compliance');
        const fileName = `Employee_Compliance_Settings_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(wb, fileName);
        showSnack('Employee compliance data exported successfully!', true);
    };

    const fieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '7px',
            fontSize: '13px',
            bgcolor: '#F9FAFB',
            '&:hover': { bgcolor: '#fff' },
            '&.Mui-focused': { bgcolor: '#fff' },
            '& fieldset': { borderColor: '#E5E7EB' },
            '&.Mui-focused fieldset': { borderColor: PRIMARY, borderWidth: '1.5px' },
        },
        '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY },
    };

    const eligibilityBlock = (settings, setSettings, accent) => {
        const months = Number(settings.eligibilityMonths) || 0;
        return (
            <Box sx={{ mt: 2, p: 1.8, borderRadius: '8px', border: '1px solid #EEF1F6', bgcolor: '#F3F8FC', opacity: settings.enabled ? 1 : 0.55 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <Switch
                            checked={Boolean(settings.eligibilityEnabled)}
                            disabled={!settings.enabled}
                            onChange={(e) => setSettings({ ...settings, eligibilityEnabled: e.target.checked })}
                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: accent }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: accent } }}
                        />
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>Apply only after a waiting period</Typography>
                            <Typography sx={{ fontSize: 11, color: '#6B7280' }}>Start this deduction only once the employee completes the months below since joining.</Typography>
                        </Box>
                    </Box>
                    <TextField
                        label="Months after joining" type="number" size="small"
                        value={settings.eligibilityMonths}
                        onChange={(e) => setSettings({ ...settings, eligibilityMonths: e.target.value })}
                        disabled={!settings.enabled || !settings.eligibilityEnabled}
                        sx={{ width: 170, ...fieldSx }}
                        slotProps={{ inputLabel: { shrink: true }, input: { endAdornment: <InputAdornment position="end">months</InputAdornment>, inputProps: { min: 0, max: 24 } } }}
                    />
                </Box>
                {settings.enabled && settings.eligibilityEnabled && (
                    <Typography sx={{ fontSize: 11, color: accent, mt: 1.2, fontWeight: 600 }}>
                        New joiners are exempt for their first {months} month{months === 1 ? '' : 's'}; the deduction starts automatically after that.
                    </Typography>
                )}
            </Box>
        );
    };

    const salaryRuleBlock = (settings, setSettings, amountKey, operator, accent, verb) => {
        const amt = Number(settings[amountKey]) || 0;
        const gte = operator === 'gte';
        return (
            <Box sx={{ mt: 2, p: 1.8, borderRadius: '8px', border: `1px solid ${accent}33`, bgcolor: `${accent}0D`, opacity: settings.enabled ? 1 : 0.55 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 800, color: accent, textTransform: 'uppercase', letterSpacing: 0.6, mb: 1 }}>Salary eligibility rule</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 13, color: '#334155', fontWeight: 600 }}>{verb} only when gross monthly salary is</Typography>
                    <Chip label={gte ? '≥  at or above' : '<  below'} size="small" sx={{ fontWeight: 800, fontSize: 12, bgcolor: `${accent}1A`, color: accent, height: 26, borderRadius: '7px' }} />
                    <TextField
                        type="number" size="small"
                        value={settings[amountKey]}
                        onChange={(e) => setSettings({ ...settings, [amountKey]: e.target.value })}
                        disabled={!settings.enabled}
                        sx={{ width: 150, ...fieldSx }}
                        slotProps={{ input: { startAdornment: <InputAdornment position="start">₹</InputAdornment>, inputProps: { min: 0 } } }}
                    />
                </Box>
                <Typography sx={{ fontSize: 11, color: accent, mt: 1, fontWeight: 600 }}>
                    {gte
                        ? `Employees earning ₹${amt.toLocaleString('en-IN')} or more are charged; anyone below is exempt.`
                        : `Employees earning under ₹${amt.toLocaleString('en-IN')} are covered; anyone at or above is exempt.`}
                </Typography>
            </Box>
        );
    };

    const handleSave = async () => {
        if (activeTab === 1) {
            if (!pfSettings.employeeContribution || !pfSettings.employerContribution) {
                showSnack('Please fill all required PF fields', false);
                return;
            }
            if (pfSettings.eligibilityEnabled && !(Number(pfSettings.eligibilityMonths) > 0)) {
                showSnack('Enter the PF waiting period in months (greater than 0)', false);
                return;
            }
            const body = {
                PFEnabled:              pfSettings.enabled,
                PFEmployeeContribution: String(pfSettings.employeeContribution),
                PFEmployerContribution: String(pfSettings.employerContribution),
                PFWageCeilingLimit:     String(pfSettings.wageLimit),
                PFAdminCharges:         String(pfSettings.adminCharges),
                PFWaitingPeriodEnabled: pfSettings.eligibilityEnabled,
                PFWaitingPeriodMonths:  pfSettings.eligibilityEnabled ? (Number(pfSettings.eligibilityMonths) || 0) : 0,
            };
            setIsPFSaving(true);
            try {
                const res = await http.post(postPFConfiguration, body);
                if (res.data && !res.data.error) {
                    showSnack('PF configuration saved successfully!', true);
                } else {
                    showSnack(res.data?.message || 'Failed to save PF configuration', false);
                }
            } catch {
                showSnack('Failed to save PF configuration. Please try again.', false);
            } finally {
                setIsPFSaving(false);
            }
            return;
        }

        if (activeTab === 2) {
            if (!esiSettings.employeeContribution || !esiSettings.employerContribution) {
                showSnack('Please fill all required ESI fields', false);
                return;
            }
            if (esiSettings.eligibilityEnabled && !(Number(esiSettings.eligibilityMonths) > 0)) {
                showSnack('Enter the ESI waiting period in months (greater than 0)', false);
                return;
            }
            const body = {
                ESIEnabled:              esiSettings.enabled,
                ESIEmployeeContribution: String(esiSettings.employeeContribution),
                ESIEmployerContribution: String(esiSettings.employerContribution),
                ESIWageCeilingLimit:     String(esiSettings.wageLimit),
                ESIWaitingPeriodEnabled: esiSettings.eligibilityEnabled,
                ESIWaitingPeriodMonths:  esiSettings.eligibilityEnabled ? (Number(esiSettings.eligibilityMonths) || 0) : 0,
            };
            setIsESISaving(true);
            try {
                const res = await http.post(postESIConfiguration, body);
                if (res.data && !res.data.error) {
                    showSnack('ESI configuration saved successfully!', true);
                } else {
                    showSnack(res.data?.message || 'Failed to save ESI configuration', false);
                }
            } catch {
                showSnack('Failed to save ESI configuration. Please try again.', false);
            } finally {
                setIsESISaving(false);
            }
            return;
        }

        if (activeTab === 3) {
            if (!ptSettings.monthlyDeduction || !ptSettings.annualDeduction) {
                showSnack('Please fill all required PT fields', false);
                return;
            }
            const body = {
                PTEnabled:            ptSettings.enabled,
                MonthlyPTDeduction:   String(ptSettings.monthlyDeduction),
                AnnualPTDeduction:    String(ptSettings.annualDeduction),
                ApplicableFromSalary: String(ptSettings.applicableFrom),
            };
            setIsPTSaving(true);
            try {
                const res = await http.post(postProfessionalTaxConfiguration, body);
                if (res.data && !res.data.error) {
                    showSnack('PT configuration saved successfully!', true);
                } else {
                    showSnack(res.data?.message || 'Failed to save PT configuration', false);
                }
            } catch {
                showSnack('Failed to save PT configuration. Please try again.', false);
            } finally {
                setIsPTSaving(false);
            }
            return;
        }

        if (activeTab === 4) {
            if (!splitValid) {
                showSnack(`The five percentages must total 100 — they currently total ${Math.round(splitTotal * 100) / 100}.`, false);
                return;
            }
            const body = {
                BasicPercent: Number(split.basicPercent) || 0,
                DaPercent: Number(split.daPercent) || 0,
                HraPercent: Number(split.hraPercent) || 0,
                ConveyancePercent: Number(split.conveyancePercent) || 0,
                SpecialPercent: Number(split.specialPercent) || 0,
            };
            setIsSplitSaving(true);
            try {
                const res = await http.post(postSalaryComponentConfig, body);
                if (res.data && !res.data.error) {
                    showSnack("Salary components saved. Applies to every payroll calculated from now on. Already-calculated payslips keep their amounts.", true);
                    fetchSalaryComponents();
                } else {
                    showSnack(res.data?.message || 'Failed to save salary components', false);
                }
            } catch (err) {
                showSnack(apiErrorMessage(err, 'Failed to save salary components. Please try again.'), false);
            } finally {
                setIsSplitSaving(false);
            }
            return;
        }

        if (activeTab === 5) {
            const body = {
                StandardDeduction: String(tdsSettings.standardDeduction),
                Section80cLimit:   String(tdsSettings.section80C),
                Section80DLimit:   String(tdsSettings.section80D),
                HRAExemption:      tdsSettings.hraExemption ? 'Y' : 'N',
            };
            setIsTDSSaving(true);
            try {
                const res = await http.post(postTDSConfiguration, body);
                if (res.data && !res.data.error) {
                    showSnack('TDS configuration saved successfully!', true);
                } else {
                    showSnack(res.data?.message || 'Failed to save TDS configuration', false);
                }
            } catch {
                showSnack('Failed to save TDS configuration. Please try again.', false);
            } finally {
                setIsTDSSaving(false);
            }
            return;
        }

        showSnack('Settings saved successfully!', true);
    };

    const SectionCard = ({ icon: Icon, title, subtitle, accent = PRIMARY, accentLight = PRIMARY_LIGHT, accentBorder = PRIMARY_BORDER, children }) => (
        <Card sx={{
            border: '1px solid #E5E7EB',
            borderRadius: '7px',
            boxShadow: 'none',
            mb: 2,
            bgcolor: '#fff',
        }}>
            <Box sx={{
                px: 2, py: 1.5,
                borderBottom: '1px solid #E5E7EB',
                bgcolor: '#fff',
                display: 'flex',
                alignItems: 'center',
                gap: 1.2,
            }}>
                <Box sx={{
                    width: 32, height: 32, borderRadius: '7px',
                    bgcolor: accentLight, border: `1px solid ${accentBorder}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <Icon sx={{ fontSize: 18, color: accent }} />
                </Box>
                <Box>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#111827', lineHeight: 1.1 }}>
                        {title}
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: '#6B7280', mt: 0.3 }}>
                        {subtitle}
                    </Typography>
                </Box>
            </Box>
            <Box sx={{ p: 2 }}>
                {children}
            </Box>
        </Card>
    );

    const ToggleRow = ({ label, description, checked, onChange, accent = PRIMARY }) => (
        <Box sx={{
            p: 1.5, mb: 2,
            borderRadius: '7px',
            bgcolor: '#F9FAFB',
            border: '1px solid #E5E7EB',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1.5,
        }}>
            <Box>
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#111827' }}>
                    {label}
                </Typography>
                <Typography sx={{ fontSize: '11px', color: '#6B7280', mt: 0.3 }}>
                    {description}
                </Typography>
            </Box>
            <Switch
                checked={checked}
                onChange={onChange}
                sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: accent },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: accent },
                }}
            />
        </Box>
    );

    const renderPFTab = () => (
        <Box>
            <SectionCard
                icon={AccountBalanceIcon}
                title="Provident Fund (PF) Configuration"
                subtitle="Configure employee and employer PF contribution rates"
                accent="#135F93" accentLight="#F4FAFE" accentBorder="#B3D3E8"
            >
                <ToggleRow
                    label="Enable PF Deduction"
                    description="Automatically deduct PF from employee salary"
                    checked={pfSettings.enabled}
                    onChange={(e) => setPfSettings({ ...pfSettings, enabled: e.target.checked })}
                    accent="#135F93"
                />
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Employee Contribution"
                            value={pfSettings.employeeContribution}
                            onChange={(e) => setPfSettings({ ...pfSettings, employeeContribution: e.target.value })}
                            size="small" fullWidth type="number"
                            disabled={!pfSettings.enabled}
                            slotProps={{
                                inputLabel: { shrink: true },
                                input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
                            }}
                            sx={fieldSx}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Employer Contribution"
                            value={pfSettings.employerContribution}
                            onChange={(e) => setPfSettings({ ...pfSettings, employerContribution: e.target.value })}
                            size="small" fullWidth type="number"
                            disabled={!pfSettings.enabled}
                            slotProps={{
                                inputLabel: { shrink: true },
                                input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
                            }}
                            sx={fieldSx}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Wage Ceiling Limit"
                            value={pfSettings.wageLimit}
                            onChange={(e) => setPfSettings({ ...pfSettings, wageLimit: e.target.value })}
                            size="small" fullWidth type="number"
                            disabled={!pfSettings.enabled}
                            slotProps={{
                                inputLabel: { shrink: true },
                                input: { startAdornment: <InputAdornment position="start">₹</InputAdornment> },
                            }}
                            sx={fieldSx}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Admin Charges"
                            value={pfSettings.adminCharges}
                            onChange={(e) => setPfSettings({ ...pfSettings, adminCharges: e.target.value })}
                            size="small" fullWidth type="number"
                            disabled={!pfSettings.enabled}
                            slotProps={{
                                inputLabel: { shrink: true },
                                input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
                            }}
                            sx={fieldSx}
                        />
                    </Grid>
                </Grid>
                {eligibilityBlock(pfSettings, setPfSettings, '#2563EB')}
            </SectionCard>
        </Box>
    );

    const renderESITab = () => (
        <Box>
            <SectionCard
                icon={SecurityIcon}
                title="Employee State Insurance (ESI) Configuration"
                subtitle="Configure ESI contribution rates and wage limits"
                accent="#0E4A75" accentLight="#E4F0F9" accentBorder="#B3D3E8"
            >
                <ToggleRow
                    label="Enable ESI Deduction"
                    description="Automatically deduct ESI if salary is below wage limit"
                    checked={esiSettings.enabled}
                    onChange={(e) => setEsiSettings({ ...esiSettings, enabled: e.target.checked })}
                    accent="#0E4A75"
                />
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Employee Contribution"
                            value={esiSettings.employeeContribution}
                            onChange={(e) => setEsiSettings({ ...esiSettings, employeeContribution: e.target.value })}
                            size="small" fullWidth type="number"
                            disabled={!esiSettings.enabled}
                            slotProps={{
                                inputLabel: { shrink: true },
                                input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
                            }}
                            sx={fieldSx}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Employer Contribution"
                            value={esiSettings.employerContribution}
                            onChange={(e) => setEsiSettings({ ...esiSettings, employerContribution: e.target.value })}
                            size="small" fullWidth type="number"
                            disabled={!esiSettings.enabled}
                            slotProps={{
                                inputLabel: { shrink: true },
                                input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
                            }}
                            sx={fieldSx}
                        />
                    </Grid>
                </Grid>
                {salaryRuleBlock(esiSettings, setEsiSettings, 'wageLimit', 'lt', '#0E4A75', 'Apply ESI')}
                {eligibilityBlock(esiSettings, setEsiSettings, '#0E4A75')}
            </SectionCard>
        </Box>
    );

    const renderPTTab = () => (
        <Box>
            <SectionCard
                icon={ReceiptIcon}
                title="Professional Tax (PT) Configuration"
                subtitle="Configure state-specific professional tax slabs"
                accent="#C2410C" accentLight="#FFF7ED" accentBorder="#FED7AA"
            >
                <ToggleRow
                    label="Enable Professional Tax"
                    description="Deduct PT as per state regulations"
                    checked={ptSettings.enabled}
                    onChange={(e) => setPtSettings({ ...ptSettings, enabled: e.target.checked })}
                    accent="#C2410C"
                />
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Monthly PT Deduction"
                            value={ptSettings.monthlyDeduction}
                            onChange={(e) => setPtSettings({ ...ptSettings, monthlyDeduction: e.target.value })}
                            size="small" fullWidth type="number"
                            disabled={!ptSettings.enabled}
                            slotProps={{
                                inputLabel: { shrink: true },
                                input: { startAdornment: <InputAdornment position="start">₹</InputAdornment> },
                            }}
                            sx={fieldSx}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Annual PT Deduction"
                            value={ptSettings.annualDeduction}
                            onChange={(e) => setPtSettings({ ...ptSettings, annualDeduction: e.target.value })}
                            size="small" fullWidth type="number"
                            disabled={!ptSettings.enabled}
                            slotProps={{
                                inputLabel: { shrink: true },
                                input: { startAdornment: <InputAdornment position="start">₹</InputAdornment> },
                            }}
                            sx={fieldSx}
                        />
                    </Grid>
                </Grid>
                {salaryRuleBlock(ptSettings, setPtSettings, 'applicableFrom', 'gte', '#C2410C', 'Deduct Professional Tax')}
            </SectionCard>
        </Box>
    );

    const renderTDSTab = () => (
        <Box>
            <SectionCard
                icon={AccountBalanceWalletIcon}
                title="Tax Deducted at Source (TDS) Configuration"
                subtitle="Configure income tax deduction settings and exemptions"
                accent="#1D4ED8" accentLight="#E4F0F9" accentBorder="#BFDBFE"
            >
                <ToggleRow
                    label="Enable TDS Calculation"
                    description="Calculate and deduct income tax as per IT Act"
                    checked={tdsSettings.enabled}
                    onChange={(e) => setTdsSettings({ ...tdsSettings, enabled: e.target.checked })}
                    accent="#1D4ED8"
                />
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Standard Deduction"
                            value={tdsSettings.standardDeduction}
                            onChange={(e) => setTdsSettings({ ...tdsSettings, standardDeduction: e.target.value })}
                            size="small" fullWidth type="number"
                            disabled={!tdsSettings.enabled}
                            slotProps={{
                                inputLabel: { shrink: true },
                                input: { startAdornment: <InputAdornment position="start">₹</InputAdornment> },
                            }}
                            sx={fieldSx}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Section 80C Limit"
                            value={tdsSettings.section80C}
                            onChange={(e) => setTdsSettings({ ...tdsSettings, section80C: e.target.value })}
                            size="small" fullWidth type="number"
                            disabled={!tdsSettings.enabled}
                            slotProps={{
                                inputLabel: { shrink: true },
                                input: { startAdornment: <InputAdornment position="start">₹</InputAdornment> },
                            }}
                            sx={fieldSx}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Section 80D Limit (Medical Insurance)"
                            value={tdsSettings.section80D}
                            onChange={(e) => setTdsSettings({ ...tdsSettings, section80D: e.target.value })}
                            size="small" fullWidth type="number"
                            disabled={!tdsSettings.enabled}
                            slotProps={{
                                inputLabel: { shrink: true },
                                input: { startAdornment: <InputAdornment position="start">₹</InputAdornment> },
                            }}
                            sx={fieldSx}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box sx={{
                            p: 1.5, height: '100%',
                            borderRadius: '7px',
                            bgcolor: '#F9FAFB',
                            border: '1px solid #E5E7EB',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}>
                            <Box>
                                <Typography sx={{ fontSize: '12.5px', fontWeight: 700, color: '#111827' }}>
                                    Enable HRA Exemption
                                </Typography>
                                <Typography sx={{ fontSize: '10.5px', color: '#6B7280', mt: 0.3 }}>
                                    Exempt House Rent Allowance from TDS
                                </Typography>
                            </Box>
                            <Switch
                                checked={tdsSettings.hraExemption}
                                onChange={(e) => setTdsSettings({ ...tdsSettings, hraExemption: e.target.checked })}
                                disabled={!tdsSettings.enabled}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#1D4ED8' },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#1D4ED8' },
                                }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </SectionCard>
        </Box>
    );

    const companyOff = (flag) => {
        if (!companyCfg) return false;
        const key = { isPFApplicable: 'pfEnabled', isESIApplicable: 'esiEnabled', isPTApplicable: 'ptEnabled' }[flag];
        return companyCfg[key] === false;
    };

    const renderEmployeeComplianceTab = () => {
        const kpiCards = [
            {
                label: 'Total Employees',
                value: stats.totalEmployees,
                sub: 'on payroll',
                color: '#135F93', bg: '#E4F0F9',
                icon: PeopleAltOutlinedIcon,
            },
            {
                label: 'PF Applicable',
                value: stats.pfApplicableCount,
                sub: `of ${stats.totalEmployees}`,
                color: '#16A34A', bg: '#DCFCE7',
                icon: AccountBalanceIcon,
            },
            {
                label: 'ESI Applicable',
                value: stats.esiApplicableCount,
                sub: `of ${stats.totalEmployees}`,
                color: '#0EA5E9', bg: '#E0F2FE',
                icon: SecurityIcon,
            },
            {
                label: 'PT Applicable',
                value: stats.ptApplicableCount,
                sub: `of ${stats.totalEmployees}`,
                color: '#F59E0B', bg: '#FFF7ED',
                icon: ReceiptIcon,
            },
        ];

        const flagColumns = [
            { flag: 'isPFApplicable', label: 'PF', accent: '#135F93' },
            { flag: 'isESIApplicable', label: 'ESI', accent: '#0E4A75' },
            { flag: 'isPTApplicable', label: 'PT', accent: '#C2410C' },
        ];

        return (
            <Box>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    {kpiCards.map((card) => {
                        const Icon = card.icon;
                        return (
                            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={card.label}>
                                <Box sx={{
                                    p: 2.5,
                                    borderRadius: '7px',
                                    bgcolor: card.bg,
                                    border: `1px solid ${card.color}22`,
                                    boxShadow: '0 1px 3px rgba(16,24,40,0.06)',
                                    height: '100%',
                                    transition: 'transform 0.15s, box-shadow 0.15s',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: `0 8px 20px ${card.color}22`,
                                    },
                                }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography sx={{
                                                fontSize: '11px', color: card.color, fontWeight: 700,
                                                textTransform: 'uppercase', letterSpacing: 0.5,
                                            }}>
                                                {card.label}
                                            </Typography>
                                            <Typography sx={{
                                                fontSize: '28px', fontWeight: 800, color: '#0F172A',
                                                lineHeight: 1.2, mt: 0.5,
                                            }} noWrap>
                                                {card.value}
                                            </Typography>
                                            <Typography sx={{
                                                fontSize: '10.5px', color: '#6B7280', fontWeight: 600, mt: 0.4,
                                            }} noWrap>
                                                {card.sub}
                                            </Typography>
                                        </Box>
                                        <Box sx={{
                                            width: 44, height: 44, borderRadius: '7px',
                                            bgcolor: '#fff', boxShadow: '0 1px 3px rgba(16,24,40,0.08)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            flexShrink: 0, ml: 1,
                                        }}>
                                            <Icon sx={{ color: card.color, fontSize: 22 }} />
                                        </Box>
                                    </Box>
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>

                <Card sx={{ border: '1px solid #E1EAF1', borderRadius: '7px', boxShadow: '0 1px 3px rgba(16,24,40,0.05)', bgcolor: '#fff', overflow: 'hidden' }}>
                    <TableContainer>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    {[
                                        { label: 'S.No' }, { label: 'Employee' }, { label: 'Pay Type' }, { label: 'Monthly Gross' },
                                        ...flagColumns.map(c => ({ label: c.label, center: true, flag: c.flag })),
                                    ].map((h) => (
                                        <TableCell
                                            key={h.label}
                                            align={h.center ? 'center' : 'left'}
                                            sx={{
                                                fontWeight: 700, fontSize: '10.5px',
                                                color: '#5B7A94',
                                                bgcolor: '#F3F8FC',
                                                textTransform: 'uppercase', letterSpacing: 0.7,
                                                whiteSpace: 'nowrap', py: 1.5,
                                                borderBottom: '1px solid #E1EAF1',
                                            }}
                                        >
                                            {h.label}
                                            {h.flag && companyOff(h.flag) && (
                                                <Tooltip arrow title={`Company ${h.label} is switched off — nothing is deducted even if applicable.`}>
                                                    <Box component="span" sx={{ color: '#C2410C', ml: 0.5, fontWeight: 800 }}>⚠</Box>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 6, borderBottom: 'none' }}>
                                            <SkeletonRows count={5} p={0} />
                                        </TableCell>
                                    </TableRow>
                                ) : filteredEmployees.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 6, borderBottom: 'none' }}>
                                            <Box sx={{
                                                width: 56, height: 56, borderRadius: '50%',
                                                bgcolor: '#F3F4F6', mx: 'auto', mb: 1.2,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            }}>
                                                <VerifiedUserIcon sx={{ fontSize: 28, color: '#9CA3AF' }} />
                                            </Box>
                                            <Typography sx={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>
                                                {searchActive ? `No results for "${searchQuery}"` : 'No employee data found'}
                                            </Typography>
                                            <Typography sx={{ fontSize: '11.5px', color: '#9CA3AF', mt: 0.4 }}>
                                                {searchActive ? 'Try a different search term' : 'Create a salary structure first — employees appear here once they have one'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : filteredEmployees.map((emp, idx) => {
                                    const avColor = avatarColorFor(emp.name || '');
                                    const isDaily = emp.payType === 'Daily';
                                    return (
                                        <TableRow
                                            key={emp.id}
                                            sx={{
                                                bgcolor: idx % 2 ? '#F3F8FC' : '#fff',
                                                '&:hover': { bgcolor: '#F3F8FC' },
                                                transition: 'background-color 0.15s',
                                            }}
                                        >
                                            <TableCell sx={{ width: 50, borderBottom: '1px solid #EEF0F6', py: 1.4 }}>
                                                <Typography sx={{ fontSize: '12px', color: '#9CA3AF', fontWeight: 500 }}>
                                                    {idx + 1}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ borderBottom: '1px solid #EEF0F6', py: 1.4 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                                                    <Avatar sx={{
                                                        width: 32, height: 32,
                                                        bgcolor: `${avColor}15`,
                                                        color: avColor,
                                                        fontSize: '11px', fontWeight: 700,
                                                        border: `1px solid ${avColor}33`,
                                                    }}>
                                                        {getInitials(emp.name || '?')}
                                                    </Avatar>
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography sx={{
                                                            fontSize: '13px', fontWeight: 600,
                                                            color: '#111827', whiteSpace: 'nowrap',
                                                        }}>
                                                            {emp.name || '—'}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: '10px', color: '#9CA3AF', fontWeight: 500 }}>
                                                            {emp.employeeId}{emp.designation ? ` · ${emp.designation}` : ''}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ borderBottom: '1px solid #EEF0F6', py: 1.4 }}>
                                                <Chip
                                                    label={isDaily ? 'Daily' : 'Monthly'}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: isDaily ? '#FFF7ED' : PRIMARY_LIGHT,
                                                        color: isDaily ? '#C2410C' : PRIMARY,
                                                        border: `1px solid ${isDaily ? '#FED7AA' : PRIMARY_BORDER}`,
                                                        fontWeight: 700, fontSize: '10.5px', height: 22,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ borderBottom: '1px solid #EEF0F6', py: 1.4 }}>
                                                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#111827' }}>
                                                    {formatINR(emp.monthlyGross)}
                                                </Typography>
                                            </TableCell>
                                            {flagColumns.map(col => (
                                                <TableCell key={col.flag} align="center" sx={{ borderBottom: '1px solid #F3F4F6', py: 0.6 }}>
                                                    <Switch
                                                        size="small"
                                                        checked={!!emp[col.flag]}
                                                        disabled={!!savingFlags[`${emp.employeeCode}:${col.flag}`]}
                                                        onChange={(e) => handleToggleFlag(emp, col.flag, e.target.checked)}
                                                        sx={{
                                                            '& .MuiSwitch-switchBase.Mui-checked': { color: col.accent },
                                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: col.accent },
                                                        }}
                                                    />
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            </Box>
        );
    };

    const renderSalaryComponentsTab = () => {
        const example = (pct) => (Math.round(23000 * (Number(pct) || 0)) / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 });
        return (
            <Box>
                <SectionCard
                    icon={ReceiptIcon}
                    title="Salary Components"
                    subtitle="How an employee's earned gross is split on the payslip. The five must total 100%."
                >
                    {splitMeta.isDefault && (
                        <Chip label="Default" size="small" sx={{ mb: 1.5, bgcolor: '#F3F4F6', color: '#6B7280', fontWeight: 700, fontSize: '10.5px', height: 22 }} />
                    )}
                    <Grid container spacing={2}>
                        {SPLIT_FIELDS.map(f => (
                            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={f.key}>
                                <TextField
                                    label={f.label}
                                    type="number"
                                    size="small"
                                    fullWidth
                                    value={split[f.key]}
                                    onChange={(e) => setSplit(prev => ({ ...prev, [f.key]: e.target.value }))}
                                    sx={fieldSx}
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                        input: { endAdornment: <InputAdornment position="end">%</InputAdornment>, inputProps: { min: 0, max: 100, step: 0.01 } },
                                    }}
                                />
                            </Grid>
                        ))}
                    </Grid>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>Total</Typography>
                        <Chip
                            label={`${Math.round(splitTotal * 100) / 100}% ${splitValid ? '✔' : '— must be 100'}`}
                            size="small"
                            sx={{
                                fontWeight: 800, fontSize: 12, height: 24,
                                bgcolor: splitValid ? '#DCFCE7' : '#FEF2F2',
                                color: splitValid ? '#16A34A' : '#DC2626',
                                border: `1px solid ${splitValid ? '#BBF7D0' : '#FECACA'}`,
                            }}
                        />
                    </Box>
                    <Typography sx={{ fontSize: 11.5, color: '#6B7280', mt: 1.5 }}>
                        Example on ₹23,000: {SPLIT_FIELDS.map(f => `${f.short} ₹${example(split[f.key])}`).join(' · ')}
                    </Typography>
                    {splitMeta.updatedBy && (
                        <Typography sx={{ fontSize: 11, color: '#9CA3AF', mt: 1 }}>
                            Last saved by {splitMeta.updatedBy}{splitMeta.updatedOn ? ` on ${splitMeta.updatedOn}` : ''}
                        </Typography>
                    )}
                </SectionCard>
            </Box>
        );
    };

    const anyConfigSaving = isPFSaving || isESISaving || isPTSaving || isTDSSaving || isSplitSaving;
    const showSaveButton = activeTab !== 0;
    const showExportButton = activeTab === 0;

    const renderToolbarControls = (h) => (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                {activeTab === 0 && (
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Search by name, ID, or designation..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ fontSize: 18, color: searchActive ? PRIMARY : '#9CA3AF' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchActive ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchQuery('')} sx={{ p: 0.3 }}>
                                            <CloseIcon sx={{ fontSize: 14, color: '#9CA3AF' }} />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                                sx: {
                                    padding: '0 12px',
                                    borderRadius: '50px',
                                    height: '32px',
                                    fontSize: '12px',
                                },
                            },
                        }}
                        sx={{
                            width: { xs: '100%', sm: 260 },
                            '& .MuiOutlinedInput-root': {
                                minHeight: '32px',
                                paddingRight: '3px',
                                backgroundColor: '#fff',
                            },
                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: websiteSettings.mainColor,
                            },
                        }}
                    />
                )}
    
                {showExportButton && (
                    <Button
                        disableElevation
                        startIcon={<SaveOutlinedIcon sx={{ fontSize: 16 }} />}
                        onClick={h.handleExportCompliance}
                        sx={{
                            textTransform: 'none', fontSize: '12.5px', fontWeight: 700,
                            color: PRIMARY_DARK, bgcolor: PRIMARY_LIGHT,
                            border: `1.5px solid ${PRIMARY_BORDER}`,
                            borderRadius: '30px',
                            px: 2.2, height: 34,
                            boxShadow: 'none',
                            '&:hover': {
                                bgcolor: '#D0E5F3',
                                borderColor: PRIMARY,
                                boxShadow: 'none',
                            },
                        }}
                    >
                        Export
                    </Button>
                )}
    
                {showSaveButton && (
                    <Button
                        variant="contained"
                        disableElevation
                        startIcon={anyConfigSaving
                            ? <CircularProgress size={14} sx={{ color: '#fff' }} />
                            : <SaveOutlinedIcon sx={{ fontSize: 18 }} />}
                        onClick={h.handleSave}
                        disabled={anyConfigSaving}
                        sx={{
                            textTransform: 'none',
                            bgcolor: '#E4F0F9',
                            color: '#135F93',
                            borderRadius: '30px',
                            fontSize: '12.5px',
                            fontWeight: 700,
                            px: 2.4, height: 34,
                            boxShadow: 'none',
                            border: '1.5px solid #B3D3E8',
                            '&:hover': {
                                bgcolor: '#D0E5F3',
                                borderColor: '#B3D3E8',
                                boxShadow: 'none',
                            },
                            '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF', borderColor: '#E5E7EB' },
                        }}
                    >
                        {anyConfigSaving ? 'Saving…' : 'Save Settings'}
                    </Button>
                )}
            </Box>
    );

    // Search, Export and Save Settings sit at the right end of the breadcrumb row, so the page starts with the section tabs.
    const setCrumbActions = useCrumbActionsSetter();
    const actionsRef = useRef({});
    actionsRef.current = { handleExportCompliance, handleSave };
    useEffect(() => {
        if (!setCrumbActions) return undefined;
        setCrumbActions(renderToolbarControls({
            handleExportCompliance: () => actionsRef.current.handleExportCompliance(),
            handleSave: () => actionsRef.current.handleSave(),
        }));
        return () => setCrumbActions(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setCrumbActions, activeTab, searchQuery, searchActive, anyConfigSaving, showExportButton, showSaveButton]);


    return (
        <>
            <SnackBar open={snackOpen} color={snackColor} setOpen={setSnackOpen} status={snackStatus} message={snackMessage} />

            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '88vh',
                px: { xs: 1.5, md: 2.5 }, pt: 2.5, pb: 3,
            }}>
                {!setCrumbActions && (
                    <Box sx={{
                        pb: 2.5,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 1.5,
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box>
                                <Typography sx={{ fontSize: '19px', fontWeight: 800, color: '#111827', letterSpacing: '-0.3px' }}>
                                    Statutory Deductions
                                </Typography>
                                <Typography sx={{ fontSize: '12px', color: '#6B7280', mt: 0.3 }}>
                                    Configure PF, ESI, Professional Tax, salary component split &amp; TDS for your workforce
                                </Typography>
                            </Box>
                        </Box>
    
                        {renderToolbarControls({ handleExportCompliance, handleSave })}
                    </Box>
                )}

                <Box sx={{
                    mb: 1.5,
                    bgcolor: '#fff',
                    border: '1px solid #E6EAF1',
                    borderRadius: '7px',
                    boxShadow: '0 1px 3px rgba(16,24,40,0.05)',
                    px: 1,
                }}>
                    <Tabs
                        value={activeTab}
                        onChange={(e, newValue) => setActiveTab(newValue)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            minHeight: 42,
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontSize: '12.5px',
                                fontWeight: 600,
                                minHeight: 42,
                                color: '#6B7280',
                                px: 2,
                            },
                            '& .Mui-selected': { color: `${PRIMARY} !important`, fontWeight: 700 },
                            '& .MuiTabs-indicator': { bgcolor: PRIMARY, height: 2.5, borderRadius: '2px 2px 0 0' },
                        }}
                    >
                        <Tab label="Employee Compliance" />
                        <Tab label="Provident Fund (PF)" />
                        <Tab label="ESI" />
                        <Tab label="Professional Tax" />
                        <Tab label="Salary Components" />
                        <Tab label="TDS" />
                    </Tabs>
                </Box>

                <Box sx={{ flex: 1 }}>
                    {activeTab === 0 && renderEmployeeComplianceTab()}
                    {activeTab === 1 && renderPFTab()}
                    {activeTab === 2 && renderESITab()}
                    {activeTab === 3 && renderPTTab()}
                    {activeTab === 4 && renderSalaryComponentsTab()}
                    {activeTab === 5 && renderTDSTab()}
                </Box>
            </Box>

        </>
    );
}
